import { Callout, Collapsible, Skeleton } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { isActionAvailable } from "@/utils/available-actions";
import type { Condition } from "@curai/js-fhir-tools";
import { useSearch } from "@tanstack/react-router";
import { Pill } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { usePrescriptions } from "../../network/usePrescriptions";
import { useCarePlanSummary } from "../network/useCarePlanSummary";
import { CareplanAvailableAction } from "../network/useCarePlanSummary";
import { useOTCPrescriptions } from "../network/useOTCPrescriptions";
import { useRxOrderEntryActions } from "../network/useRxOrderEntryActions";
import { AddMedication } from "./AddMedication";
import { EligibilityButton } from "./Eligibility/EligibilityButton";
import { Medication } from "./Medication";
import { MedicationDetails } from "./MedicationDetails";
import { MissingPatientDetailsCallout } from "./MissingPatientDetailsCallout";
import { OTCMedication } from "./OTCMedication";
import { ProtocolSuggestion } from "./ProtocolSuggestion/ProtocolSuggestion";
import { SendAllToPharmacyButton } from "./SendAllToPharmacyButton";

interface MedicationsProps {
  patientId: string;
  encounterId: string | number;
  carePlanId: string;
  eocId?: string;
  totalMedications: number;
  condition?: Condition;
  conditions?: Condition[];
  isAmazon?: boolean;
}

export const Medications = ({
  patientId,
  encounterId: rawEncounterId,
  carePlanId,
  eocId,
  totalMedications,
  condition,
  conditions,
  isAmazon = false,
}: MedicationsProps) => {
  const { encounterId: routeEncounterId } = useSearch({
    from: "/patient/$patientId",
  });
  const encounterId = rawEncounterId.toString();
  const motherEncounterId = routeEncounterId?.toString();
  const isMotherEncounter = encounterId === motherEncounterId;
  const [open, setOpen] = useState(true);
  const { data: rxOrderEntryActions } = useRxOrderEntryActions({
    patientId,
  });
  const {
    data: prescriptions,
    isLoading,
    isError,
  } = usePrescriptions({
    patientId: patientId,
    encounterId,
    enabled: open,
  });
  const { data: otcPrescriptions } = useOTCPrescriptions({
    patientId,
    encounterId,
    enabled: open,
  });

  const { data: careplanSummary } = useCarePlanSummary(encounterId);
  const careplanAvailableActions = careplanSummary?.availableActions;
  const canReadPrescriptions = isActionAvailable(
    CareplanAvailableAction.READ_PRESCRIPTION,
    careplanAvailableActions,
  );
  const canAddMedications = isActionAvailable(
    CareplanAvailableAction.ADD_PRESCRIPTION,
    careplanAvailableActions,
  );

  const currentPrescriptionCodes = useMemo(() => {
    return (
      prescriptions?.rxs.flatMap((rx) => {
        const codings =
          rx.medicationRequest.medicationCodeableConcept?.coding ?? [];
        return codings
          .map((coding) => coding.code)
          .filter((code): code is string => Boolean(code));
      }) ?? []
    );
  }, [prescriptions?.rxs]);

  if (!canReadPrescriptions) {
    return null;
  }

  return (
    <Collapsible
      title="Prescriptions"
      titleIcon={<Pill className="w-4 h-4" />}
      headerSize="sm"
      onOpenChange={(open) => {
        trackEvent(
          open ? TRACKERS.EXPAND_MEDICATION : TRACKERS.COLLAPSE_MEDICATION,
        );
        setOpen(open);
      }}
      status={totalMedications ? `${totalMedications.toString()}` : "None"}
      open={open}
      controlled={true}
      customActions={[
        isMotherEncounter && (
          <Fragment key="send-all-to-pharmacy">
            <SendAllToPharmacyButton encounterId={encounterId} mode="icon" />
          </Fragment>
        ),
        <EligibilityButton key="eligibility" />,
      ]}
    >
      <div className="grid gap-2">
        {isError && (
          <Callout
            title="Something went wrong"
            description="Unable to load medications"
            variant="danger"
          />
        )}

        {isLoading &&
          Array.from({ length: 3 }).map((_, index) => (
            //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
            <Skeleton className="h-8" key={index} />
          ))}
        {!isLoading && !prescriptions?.registeredWithErxVendor && (
          <MissingPatientDetailsCallout
            missingPatientDetails={prescriptions?.missingPatientDetails ?? []}
            heightWeightNeeded={prescriptions?.heightWeightNeeded ?? false}
            patientId={patientId}
          />
        )}
        {prescriptions?.rxs.map((rxItem) => {
          if (rxItem.isSentToPharmacy && !rxItem.canRetrySendToPharmacy) {
            return (
              <MedicationDetails
                medicationRequest={rxItem.medicationRequest}
                key={rxItem.medicationRequest.id}
              />
            );
          }
          return (
            <Medication
              key={rxItem.medicationRequest.id}
              rxItem={rxItem}
              readOnly={!canAddMedications}
              registeredWithErxVendor={prescriptions?.registeredWithErxVendor}
              isAmazon={isAmazon}
              // default the last added medication to open if not already completed
              defaultOpen={true}
            />
          );
        })}
        {otcPrescriptions?.otcPrescriptions.map((otcPrescription) => {
          return (
            <OTCMedication
              key={otcPrescription.activityId}
              patientId={patientId}
              encounterId={encounterId}
              carePlanId={carePlanId}
              otcPrescription={otcPrescription}
              readOnly={!canAddMedications}
            />
          );
        })}
        {isMotherEncounter && (
          <SendAllToPharmacyButton encounterId={encounterId} />
        )}
        {rxOrderEntryActions?.hasPrescribePrivs && (
          <AddMedication
            encounterId={encounterId}
            carePlanId={carePlanId}
            condition={condition}
            conditions={conditions}
            otcAllowed={rxOrderEntryActions.otcAllowed}
          />
        )}

        {!isLoading && !isError && canAddMedications && (
          <ProtocolSuggestion
            patientId={patientId}
            encounterId={encounterId}
            carePlanId={carePlanId}
            eocId={eocId}
            currentPrescriptionCodes={currentPrescriptionCodes}
          />
        )}
      </div>
    </Collapsible>
  );
};
